import { useState, useEffect, useMemo } from 'react';
import { FaServer, FaDollarSign, FaChartBar } from 'react-icons/fa';
import Table from '../components/common/Table.jsx';
import dayjs from 'dayjs';
import BillingApiService from '../services/BillingApiService';
import DebugService from '../services/DebugService';
import SnackbarNotification, { SNACKBAR_THEME } from '../components/common/SnackbarNotification';
import { CONST_SITE_LABEL, PLATFORM, STORAGE_KEYS, toWholeString, setScopedItem, buildExportFilename, ADD_NEW_ACCOUNT } from '../components/common/Helper';
import { useAuth } from '../state/AuthContext';
import { useScopedLocalStorage } from '../hooks/useScopedLocalStorage';

/* ─── Dimension lists (exact copy from old component) ─────── */
const DIMENSION_LIST = [
  { label: 'Availability Zone',  value: 'AZ' },
  { label: 'Service',            value: 'SERVICE' },
  { label: 'Tag',                value: 'TAG' },
  { label: 'Instance Type',      value: 'INSTANCE_TYPE' },
  { label: 'Linked Account',     value: 'LINKED_ACCOUNT' },
  { label: 'Usage Type',         value: 'USAGE_TYPE' },
  { label: 'Platform',           value: 'PLATFORM' },
  { label: 'Region',             value: 'REGION' },
  { label: 'Legal Entity Name',  value: 'LEGAL_ENTITY_NAME' },
  { label: 'Billing Entity',     value: 'BILLING_ENTITY' },
  { label: 'Tenancy',            value: 'TENANCY' },
  { label: 'Purchase Type',      value: 'PURCHASE_TYPE' },
  { label: 'Operation',          value: 'OPERATION' },
  { label: 'Database Engine',    value: 'DATABASE_ENGINE' },
  { label: 'Record Type',        value: 'RECORD_TYPE' },
];

const AZURE_DIMENSION_LIST = [
  { label: 'Charge Type',        value: 'CHARGE_TYPE' },
  { label: 'Frequency',          value: 'FREQUENCY' },
  { label: 'Meter',              value: 'METER' },
  { label: 'Meter Category',     value: 'METER_CATEGORY' },
  { label: 'Meter Subcategory',  value: 'METER_SUBCATEGORY' },
  { label: 'Pricing Model',      value: 'PRICING_MODEL' },
  { label: 'Product',            value: 'PRODUCT' },
  { label: 'Provider',           value: 'PROVIDER' },
  { label: 'Publisher Type',     value: 'PUBLISHER_TYPE' },
  { label: 'Resource Location',  value: 'LOCATION' },
  { label: 'Reservation ID',     value: 'RESERVATION_ID' },
  { label: 'Resource Group',     value: 'RESOURCE_GROUP' },
  { label: 'Resource Type',      value: 'RESOURCE_TYPE' },
  { label: 'Service Family',     value: 'SERVICE_FAMILY' },
  { label: 'Service Name',       value: 'SERVICE_NAME' },
  { label: 'Tag',                value: 'TAG' },
  { label: 'Unit Of Measure',    value: 'UNIT_OF_MEASURE' },
];

/* ─── Shared field wrapper (floating label) ───────────────── */
const Field = ({ label, children, wide }) => (
  <div style={{
    position: 'relative', background: '#fff', border: '1px solid #e6e9f0',
    borderRadius: 12, padding: '10px 40px 10px 16px',
    minWidth: wide ? 220 : 180, flex: wide ? 1 : undefined,
  }}>
    <span style={{ fontSize: 10.5, color: '#6b7280', position: 'absolute', top: -8, left: 12, background: '#fff', padding: '0 5px', pointerEvents: 'none' }}>
      {label}
    </span>
    {children}
  </div>
);

const SELECT_STYLE = {
  border: 'none', outline: 'none', background: 'transparent',
  fontSize: 14, fontWeight: 600, color: '#1f2937', width: '100%', cursor: 'pointer',
  appearance: 'none', WebkitAppearance: 'none',
};

const CARET = (
  <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: '#6b7280', pointerEvents: 'none', fontSize: 13 }}>▾</span>
);

/* ─── Component ───────────────────────────────────────────── */
export default function CostExplorer() {
  const { selectedAccount, selectedPlatform } = useAuth();
  const { getScopedItem } = useScopedLocalStorage();

  const currentDate    = new Date();
  const sixMonthsBefore = new Date(currentDate.getFullYear(), currentDate.getMonth() - 6, currentDate.getDate());
  const lastMonth       = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, currentDate.getDate());
  const last13Month     = new Date(currentDate.getFullYear() - 1, currentDate.getMonth() - 1, currentDate.getDate());

  const [data, setData]                         = useState([]);
  const [tagList, setTagList]                   = useState([]);
  const [blockData, setBlockData]               = useState({ totalServicesCostValue: '0.00', totalAverageMonthlyCostValue: '0.00', totalServices: 0 });
  const [isLoading, setIsLoading]               = useState(false);
  const [snackbarMessage, setSnackbarMessage]   = useState(null);
  const [snackbarStatus, setSnackbarStatus]     = useState(null);
  const [costExplorerApiData, setCostExplorerApiData] = useState({});
  const [searchQuery, setSearchQuery]           = useState('');
  const [dataFilters, setDataFilters]           = useState({
    startMonthDate:  dayjs(sixMonthsBefore).startOf('month'),
    endMonthDate:    dayjs(lastMonth).endOf('month'),
    granularity:     'MONTHLY',
    dimension:       selectedPlatform === PLATFORM.AWS ? 'SERVICE' : 'SERVICE_NAME',
    filter:          '',
    filterTagValues: [],
  });

  /* ─── fetchData ─────────────────────────────────────────── */
  const fetchData = () => {
    if (!selectedAccount || selectedAccount === ADD_NEW_ACCOUNT) return;
    setData([]);
    setBlockData({ totalServicesCostValue: '0.00', totalAverageMonthlyCostValue: '0.00', totalServices: 0 });
    setIsLoading(true);

    const dataRequest = {
      ...dataFilters,
      userAccountId: selectedAccount,
      type: 'COST_EXPLORER',
    };
    dataRequest['startMonthDate'] = dataRequest['startMonthDate']?.format('YYYY-MM-DD');
    dataRequest['endMonthDate']   = dataRequest['endMonthDate']?.format('YYYY-MM-DD');
    delete dataRequest['filterTagValues'];

    if (selectedPlatform === PLATFORM.AZURE) {
      dataRequest['filterKey'] = dataFilters['filter'];
      delete dataRequest['filter'];

      BillingApiService.getAzureCostExplorerData(dataRequest)
        .then(res => {
          DebugService.log('Azure Cost Explorer: API response', res);
          setCostExplorerApiData(res?.data ? res : {});
          const apiData = res['data'] ?? [];

          if (res['totalMonthlyCost']) {
            setBlockData({
              totalServicesCostValue:      parseFloat(res['totalMonthlyCost']['totalServicesCostValue'] ?? '0.00').toFixed(2),
              totalAverageMonthlyCostValue: parseFloat(res['totalMonthlyCost']['totalAverageCostValue']  ?? '0.00').toFixed(2),
              totalServices:               res['totalMonthlyCost']['totalServices'] ?? 0,
            });
          }

          if (apiData.length > 0) {
            setData(apiData.sort((a, b) => b['totalCostValue'] - a['totalCostValue']).map(s => ({ ...s, id: s['service'] })));
          }

          if (res['error']) DebugService.error('Azure Cost Explorer: Error', res['error']);
          setIsLoading(false);
        })
        .catch(err => { DebugService.error('Azure Cost Explorer: Error', err); setIsLoading(false); });

    } else {
      dataRequest['filterKey']    = dataFilters['filter']?.['key'];
      dataRequest['filterValues'] = dataFilters['filterTagValues'];
      delete dataRequest['filter'];

      DebugService.log('Cost Explorer: Data request', dataRequest);

      BillingApiService.getCostExplorerData(dataRequest)
        .then(res => {
          DebugService.log('Cost Explorer: API response', res);
          setCostExplorerApiData(res?.data ? res : {});
          const apiData = res['data'] ?? [];

          if (res['totalPriceValue']) {
            setBlockData({
              totalServicesCostValue:      parseFloat(res['totalPriceValue']['totalServicesCostValue'] ?? '0.00').toFixed(2),
              totalAverageMonthlyCostValue: parseFloat(res['totalPriceValue']['totalAverageCostValue']  ?? '0.00').toFixed(2),
              totalServices:               res['totalPriceValue']['totalServices'] ?? 0,
            });
          }

          if (apiData.length > 0) {
            setData(apiData.sort((a, b) => b['totalCostValue'] - a['totalCostValue']).map(s => ({ ...s, id: s['service'] })));
          }

          if (res['error']) DebugService.error('Cost Explorer: Error', res['error']);
          setIsLoading(false);
        })
        .catch(err => { DebugService.error('Cost Explorer: Error', err); setIsLoading(false); });
    }
  };

  /* ─── fetchTagList ──────────────────────────────────────── */
  const fetchTagList = () => {
    if (!selectedAccount || selectedAccount === ADD_NEW_ACCOUNT) return;
    if (selectedPlatform === PLATFORM.AZURE) {
      BillingApiService.getAzureTagList(selectedAccount, dataFilters)
        .then(res => {
          DebugService.log('Azure Cost Explorer: Tag list', res);
          setTagList(res['data']['tags'] ?? []);
          if (res['error']) DebugService.error('Azure Cost Explorer: Tag list error', res['error']);
        })
        .catch(err => DebugService.error('Azure Cost Explorer: Tag list error', err));
    } else {
      BillingApiService.getTagList(selectedAccount, dataFilters)
        .then(res => {
          DebugService.log('Cost Explorer: Tag list', res);
          setTagList(res['data']['tags'] ?? []);
          if (res['error']) DebugService.error('Cost Explorer: Tag list error', res['error']);
        })
        .catch(err => DebugService.error('Cost Explorer: Tag list error', err));
    }
  };

  /* ─── useEffects ────────────────────────────────────────── */
  useEffect(() => {
    document.title = `${CONST_SITE_LABEL} | Cost Explorer`;
  }, []);

  // Re-fetch tag list whenever the account changes
  useEffect(() => { fetchTagList(); }, [selectedAccount]);

  // Fetch data on any filter or account change (also fires on mount)
  useEffect(() => { fetchData(); }, [dataFilters, selectedAccount]);

  // When dimension changes: reset filter + tag values inline (no separate cascade effect)
  useEffect(() => {
    if (dataFilters.dimension === 'TAG') {
      const firstTag = tagList.length > 0 ? tagList[0] : '';
      setDataFilters(prev => ({ ...prev, filter: firstTag, filterTagValues: [] }));
    } else {
      setDataFilters(prev => ({ ...prev, filter: '', filterTagValues: [] }));
    }
  }, [dataFilters.dimension]);

  // If tagList arrives after dimension was already set to TAG, auto-select first tag
  useEffect(() => {
    if (tagList.length > 0 && dataFilters.dimension === 'TAG' && !dataFilters.filter) {
      setDataFilters(prev => ({ ...prev, filter: tagList[0] }));
    }
  }, [tagList]);

  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, costExplorerApiData);
  }, [costExplorerApiData]);

  /* ─── Derived table data ─────────────────────────────────── */
  const dateKeys = useMemo(
    () => data.length > 0 && data[0]?.costDates ? Object.keys(data[0].costDates) : [],
    [data],
  );

  const tableColumns = useMemo(() => {
    if (data.length === 0) return [];
    return [
      {
        key: 'service',
        label: toWholeString(dataFilters.dimension ?? 'Service'),
        render: (row) => (
          <div title={row.service} style={{ fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260 }}>
            {row.service || '—'}
          </div>
        ),
      },
      {
        key: 'totalCostValue',
        label: 'Total Cost',
        render: (row) => {
          const val = parseFloat(row.totalCostValue ?? 0);
          return (
            <span style={{ fontWeight: 700, fontSize: 13, color: val < 0 ? '#ef4444' : '#1f2937', display: 'block', textAlign: 'center' }}>
              ${val.toFixed(3)}
            </span>
          );
        },
      },
      ...dateKeys.map(d => ({
        key: d,
        label: d,
        render: (row) => {
          const val = parseFloat(row.costDates?.[d] ?? 0);
          return (
            <span style={{ fontVariantNumeric: 'tabular-nums', color: val === 0 ? '#9ca3af' : '#374151' }}>
              ${val.toFixed(2)}
            </span>
          );
        },
      })),
    ];
  }, [data, dataFilters.dimension, dateKeys]);

  const tableRows = useMemo(
    () => data.map(row => ({ id: row.service, ...row })),
    [data],
  );

  const filteredRows = useMemo(() => {
    if (!searchQuery) return tableRows;
    const q = searchQuery.toLowerCase();
    return tableRows.filter(r => (r.service || '').toLowerCase().includes(q));
  }, [tableRows, searchQuery]);

  /* ─── Totals ─────────────────────────────────────────────── */
  const totals = useMemo(() => {
    if (data.length === 0 || dateKeys.length === 0) return null;
    const totalCost = data.reduce((s, r) => s + parseFloat(r.totalCostValue ?? 0), 0);
    const dateTotals = Object.fromEntries(
      dateKeys.map(d => [d, data.reduce((s, r) => s + parseFloat(r.costDates?.[d] ?? 0), 0)])
    );
    return { totalCost, dateTotals };
  }, [data, dateKeys]);

  /* ─── Date range label ───────────────────────────────────── */
  const dateRangeLabel = `${dataFilters.startMonthDate?.format('MMM YYYY') ?? ''} – ${dataFilters.endMonthDate?.format('MMM YYYY') ?? ''} · ${dataFilters.granularity.toLowerCase()}`;

  const showTagFilter = (selectedPlatform === PLATFORM.AWS || (selectedPlatform === PLATFORM.AZURE && dataFilters.dimension === 'TAG')) && tagList.length > 0;
  const showTagValueFilter = selectedPlatform === PLATFORM.AWS && tagList.length > 0 && dataFilters.filter !== '';
  const dimList = selectedPlatform === PLATFORM.AZURE ? AZURE_DIMENSION_LIST : DIMENSION_LIST;

  /* ─── Render ──────────────────────────────────────────────── */
  return (
    <div style={{ padding: '24px 28px 56px', background: '#f4f6fa', minHeight: '100vh' }}>

      {/* ── Filters ─────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 26 }}>

        {/* Start Date */}
        <Field label="Start Date">
          <input
            type="date"
            value={dataFilters.startMonthDate?.format('YYYY-MM-DD') ?? ''}
            min={dayjs(last13Month).format('YYYY-MM-DD')}
            max={dayjs().format('YYYY-MM-DD')}
            onChange={(e) => setDataFilters(prev => ({ ...prev, startMonthDate: dayjs(e.target.value).startOf('month') }))}
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 14, fontWeight: 600, color: '#1f2937', width: '100%', cursor: 'pointer' }}
          />
        </Field>

        {/* End Date */}
        <Field label="End Date">
          <input
            type="date"
            value={dataFilters.endMonthDate?.format('YYYY-MM-DD') ?? ''}
            min={dataFilters.startMonthDate?.format('YYYY-MM-DD')}
            max={dayjs().format('YYYY-MM-DD')}
            onChange={(e) => setDataFilters(prev => ({ ...prev, endMonthDate: dayjs(e.target.value).endOf('month') }))}
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 14, fontWeight: 600, color: '#1f2937', width: '100%', cursor: 'pointer' }}
          />
        </Field>

        {/* Granularity */}
        <Field label="Granularity">
          <select
            value={dataFilters.granularity}
            onChange={(e) => setDataFilters(prev => ({ ...prev, granularity: e.target.value }))}
            style={SELECT_STYLE}
          >
            <option value="MONTHLY">Monthly</option>
          </select>
          {CARET}
        </Field>

        {/* Dimension */}
        <Field label="Dimension">
          <select
            value={dataFilters.dimension}
            onChange={(e) => setDataFilters(prev => ({ ...prev, dimension: e.target.value }))}
            style={SELECT_STYLE}
          >
            {dimList.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
          {CARET}
        </Field>

        {/* Choose Tag */}
        {showTagFilter && (
          <Field label="Choose Tag" wide>
            <select
              value={typeof dataFilters.filter === 'object' ? dataFilters.filter?.key ?? '' : dataFilters.filter ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                const match = tagList.find(t => (selectedPlatform === PLATFORM.AZURE ? t : t['key']) === raw);
                setDataFilters(prev => ({ ...prev, filter: match ?? raw, filterTagValues: [] }));
              }}
              style={SELECT_STYLE}
            >
              {dataFilters.dimension !== 'TAG' && <option value="">Select</option>}
              {tagList.map(t => {
                const label = selectedPlatform === PLATFORM.AZURE ? t : t['key'];
                return <option key={label} value={label}>{label}</option>;
              })}
            </select>
            {CARET}
          </Field>
        )}

        {/* Choose Tag Value (AWS only) */}
        {showTagValueFilter && (
          <Field label="Choose Tag Value" wide>
            <select
              multiple
              value={dataFilters.filterTagValues}
              onChange={(e) => {
                const vals = Array.from(e.target.selectedOptions, o => o.value);
                setDataFilters(prev => ({ ...prev, filterTagValues: vals }));
              }}
              style={{ ...SELECT_STYLE, height: 36 }}
            >
              {dataFilters.filter?.values?.map(tv => (
                <option key={tv} value={tv}>{tv}</option>
              ))}
            </select>
            {CARET}
          </Field>
        )}
      </div>

      {/* ── Stat Cards ──────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 20 }}>
        {[
          { label: 'Total Services',        value: blockData.totalServices,               prefix: '',  icon: <FaServer />,  bg: '#eef4ff', ibg: '#dbe8ff', icolor: '#3b82f6', numColor: '#1d4ed8' },
          { label: 'Total Cost',            value: blockData.totalServicesCostValue,      prefix: '$', icon: <FaDollarSign />, bg: '#e9f9f1', ibg: '#cdf2e1', icolor: '#10b981', numColor: '#047857' },
          { label: 'Average Monthly Cost',  value: blockData.totalAverageMonthlyCostValue, prefix: '$', icon: <FaChartBar />, bg: '#fdecec', ibg: '#fbd5d5', icolor: '#ef4444', numColor: '#1d4ed8' },
        ].map(card => (
          <div key={card.label} style={{ borderRadius: 12, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12, background: card.bg, boxShadow: '0 1px 2px rgba(16,24,40,.04),0 4px 14px rgba(16,24,40,.05)', border: '1px solid rgba(0,0,0,.02)' }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, background: card.ibg, color: card.icolor, flexShrink: 0 }}>
              {card.icon}
            </div>
            <div>
              <div style={{ fontSize: 24, fontWeight: 750, letterSpacing: '-.02em', color: card.numColor, marginTop: 1 }}>
                {isLoading ? '—' : `${card.prefix}${Number(card.value).toLocaleString()}`}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>{card.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Cost Table ──────────────────────────────────────── */}
      <Table
        rows={filteredRows}
        columns={tableColumns}
        loading={isLoading}
        defaultPageSize={25}
        pageSizes={[10, 25, 50, 100]}
        defaultSortKey="totalCostValue"
        defaultSortDir="desc"
        exportFilename={buildExportFilename('cost_explorer', selectedAccount)}
        emptyText="No cost data found for the selected filters."
        headerExtra={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <div>
              <span style={{ fontSize: 15, fontWeight: 700 }}>Cost by Service</span>
              <span style={{ fontSize: 12.5, color: '#6b7280', marginLeft: 10 }}>{dateRangeLabel}</span>
            </div>
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="⌕  Search service…"
              style={{ background: '#f8fafc', border: '1px solid #e6e9f0', borderRadius: 10, padding: '7px 14px', fontSize: 13, color: '#6b7280', minWidth: 220, outline: 'none' }}
            />
          </div>
        }
        subscriptionGated
      />

      {/* Snackbar */}
      {snackbarStatus && (
        <SnackbarNotification
          initialOpen={true} duration={5000} message={snackbarMessage}
          actionButtonName="Ok"
          theme={snackbarStatus === 'success' ? SNACKBAR_THEME.GREEN : SNACKBAR_THEME.RED}
          yPosition="top" xPosition="center"
        />
      )}
    </div>
  );
}
